import {
    Box,
    ButtonGroup,
    Container,
    Flex,
    Heading,
    IconButton,
    Td,
    Tr,
    Badge,
    Text
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { FaPrint } from "react-icons/fa";
import { Form, Searchable, Table } from "components";
import { Moment } from "config";
import { useEffect, useState } from "react";
import useBestReferral from "hooks/useBestReferral";

export default function BestReferral() {
    const minDate = Moment("2026-05-01", "YYYY-MM-DD").toDate();
    const initial = [minDate, Moment().toDate()];

    const [period, setPeriod] = useState(initial);
    const [page, setPage] = useState(1);
    const [term, setTerm] = useState("");

    const validPeriod = period &&
    period[0] instanceof Date &&
    period[1] instanceof Date
        ? period
        : null;

    const bestReferrals = useBestReferral(term, validPeriod);
    const hasData = bestReferrals?.data?.length > 0;

    const handlePeriodChange = (value) => {
        setPeriod(value);
    };

    useEffect(() => {
        setPage(1);
    }, [term, period]);

    // Funções ajustadas para usar a position vinda da API
    const getMedalOrNumber = (position) => {
        switch (position) {
            case 1:
                return "🥇 1º";
            case 2:
                return "🥈 2º";
            case 3:
                return "🥉 3º";
            default:
                return `${position}º`;
        }
    };

    const getMedalIcon = (position) => {
        switch (position) {
            case 1:
                return "🥇";
            case 2:
                return "🥈";
            case 3:
                return "🥉";
            default:
                return null;
        }
    };

    const getColorScheme = (position) => {
        switch (position) {
            case 1:
                return "yellow";
            case 2:
                return "gray";
            case 3:
                return "orange";
            default:
                return "green";
        }
    };

    const getPositionColorScheme = (position) => {
        switch (position) {
            case 1:
                return "yellow";
            case 2:
                return "gray";
            case 3:
                return "orange";
            default:
                return "blue";
        }
    };

    const columns = [
        { children: "Posição", style: { textAlign: 'center' } },
        { children: "Código", style: { textAlign: 'center' } },
        { children: "Consultor" },
        { children: "Cidade", style: { textAlign: 'center' } },
        { children: "Estado", style: { textAlign: 'center' } },
        { children: "Total de Registros", isNumeric: true, style: { textAlign: 'center' } },
    ];

    const handleExportCsv = () => {
        if (!bestReferrals?.data?.length) return;

        const rows = bestReferrals?.data;
        const header = ["Posição", "Código", "Consultor", "Cidade", "Estado", "Total de Registros"];

        const csvContent = [
            header.join(";"),
            ...rows.map((item) => {
                const position = getMedalOrNumber(item.position);
                return [
                    position,
                    item.consultant_code,
                    item.consultant_name,
                    item.city_name || "-",
                    item.state_name || "-",
                    item.total_registrations,
                ].join(";");
            })
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "relatorio_best_referral.csv";
        link.click();
    };

    return (
        <Container maxW="90%" mt={10}>
            <Flex justifyContent="space-between" alignItems="center">
                <Heading>Relatório - Top 10 Recrutadores BNF</Heading>

                <ButtonGroup size="xs">
                    <IconButton
                        icon={<RepeatIcon />}
                        aria-label="Recarregar"
                        isLoading={bestReferrals.isFetching}
                        onClick={bestReferrals.refetch}
                    />

                    <IconButton
                        icon={<FaPrint />}
                        aria-label="Imprimir"
                        isDisabled={bestReferrals.isFetching || !hasData}
                        colorScheme="blue"
                        onClick={() => handleExportCsv()}
                    />
                </ButtonGroup>
            </Flex>

            <Flex mt={2} w="100%" align="center" gap={2}>
                <Box flex={1}>
                    <Searchable
                        value={term}
                        setValue={setTerm}
                        placeholder="Pesquisar por nome do consultor..."
                    />
                </Box>

                <Form.Interval
                    initial={initial}
                    onSelect={handlePeriodChange}
                    /*minDate={minDate}*/
                />
            </Flex>

            {!hasData && !bestReferrals.isFetching ? (
                <Flex justify="center" align="center" mt={10} p={10}>
                    <Text fontSize="lg" color="gray.500">
                        Nenhum Recrutador até o momento
                    </Text>
                </Flex>
            ) : (
                <Table
                    mt={5}
                    columns={columns}
                    setPage={setPage}
                >
                    {bestReferrals?.data?.map((item, idx) => (
                        <Tr
                            key={idx}
                            cursor="pointer"
                            _hover={{ backgroundColor: "rgba(0,0,0,0.03)" }}
                        >
                            <Td style={{ textAlign: 'center' }}>
                                <Badge
                                    colorScheme={getPositionColorScheme(item.position)}
                                    variant="subtle"
                                    fontSize="1em"
                                >
                                    {getMedalOrNumber(item.position)}
                                </Badge>
                            </Td>

                            <Td style={{ textAlign: 'center' }}>
                                <Badge
                                    colorScheme="blue"
                                    variant="subtle"
                                    fontSize="1em"
                                >
                                    {item.consultant_code}
                                </Badge>
                            </Td>

                            <Td textTransform="uppercase">
                                {item.consultant_name}
                            </Td>

                            <Td style={{ textAlign: 'center' }}>
                                {item.city_name || "-"}
                            </Td>

                            <Td style={{ textAlign: 'center' }}>
                                {item.state_name || "-"}
                            </Td>

                            <Td
                                isNumeric
                                style={{ textAlign: 'center' }}
                            >
                                <Badge
                                    colorScheme={getColorScheme(item.position)}
                                    variant="subtle"
                                    fontSize="1em"
                                >
                                    {getMedalIcon(item.position)} {item.total_registrations}
                                </Badge>
                            </Td>
                        </Tr>
                    ))}
                </Table>
            )}
        </Container>
    );
}